/**
 * Centralized API Client for ZuperBooks
 * Handles all HTTP requests with consistent configuration
 * Build version: 2026-01-15-v2
 */

import axios from 'axios';
import { config } from '../config/environment';

// Default tenant ID - can be overridden by localStorage or session
const getDefaultTenantId = () => {
  return localStorage.getItem('tenantId') || 'tenant-123';
};

/**
 * Create axios instance with default configuration
 */
const createApiClient = () => {
  const instance = axios.create({
    baseURL: config.backendUrl,
    timeout: 30000, // 30 seconds
    // IMPORTANT: withCredentials must be false when backend uses Access-Control-Allow-Origin: *
    // Using credentials with wildcard origin is not allowed by browsers (CORS spec)
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'X-Tenant-ID': getDefaultTenantId(),
    },
  });

  // Request interceptor - Enhanced logging for debugging
  instance.interceptors.request.use(
    (requestConfig) => {
      // Always ensure X-Tenant-ID is set on every request
      requestConfig.headers['X-Tenant-ID'] = getDefaultTenantId();
      
      const fullUrl = requestConfig.baseURL + requestConfig.url;
      
      // DEBUG: Log the exact URL being requested
      console.log('🔵 API REQUEST [v2]:', {
        method: requestConfig.method?.toUpperCase(),
        fullUrl: fullUrl,
        baseURL: requestConfig.baseURL,
        endpoint: requestConfig.url,
        urlLength: requestConfig.url?.length,
        environment: config.environment,
      });
      
      return requestConfig;
    },
    (error) => {
      console.error('❌ REQUEST ERROR:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor - Enhanced logging and error handling
  instance.interceptors.response.use(
    (response) => {
      console.log('✅ API RESPONSE:', {
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
        statusText: response.statusText,
      });
      
      return response;
    },
    (error) => {
      // Enhanced error logging
      const errorDetails = {
        url: error.config?.url,
        fullUrl: error.config?.baseURL + error.config?.url,
        method: error.config?.method?.toUpperCase(),
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data,
        environment: config.environment,
      };
      
      console.error('❌ API ERROR:', errorDetails);
      
      // Specific error handling
      if (error.response?.status === 401) {
        console.warn('⚠️ 401 Unauthorized - Authentication required');
      } else if (error.response?.status === 403) {
        console.warn('⚠️ 403 Forbidden - Permission denied');
      } else if (error.response?.status === 404) {
        console.warn('⚠️ 404 Not Found - Endpoint does not exist');
      } else if (error.response?.status === 500) {
        console.error('⚠️ 500 Server Error - Backend issue');
      } else if (error.message === 'Network Error') {
        console.error('⚠️ Network Error - Cannot reach backend at:', error.config?.baseURL);
        console.error('   Check if backend is running and CORS is configured');
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// Create the main API client
export const apiClient = createApiClient();

/**
 * Generic API wrapper with consistent error handling
 */
export const api = {
  /**
   * GET request
   * @param {string} url - Endpoint URL
   * @param {Object} config - Axios config
   * @returns {Promise} Response data
   */
  get: async (url, config = {}) => {
    try {
      const response = await apiClient.get(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * POST request
   * @param {string} url - Endpoint URL
   * @param {Object} data - Request body
   * @param {Object} config - Axios config
   * @returns {Promise} Response data
   */
  post: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.post(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * PUT request
   * @param {string} url - Endpoint URL
   * @param {Object} data - Request body
   * @param {Object} config - Axios config
   * @returns {Promise} Response data
   */
  put: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.put(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * PATCH request
   * @param {string} url - Endpoint URL
   * @param {Object} data - Request body
   * @param {Object} config - Axios config
   * @returns {Promise} Response data
   */
  patch: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.patch(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * DELETE request
   * @param {string} url - Endpoint URL
   * @param {Object} config - Axios config
   * @returns {Promise} Response data
   */
  delete: async (url, config = {}) => {
    try {
      const response = await apiClient.delete(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

/**
 * Authentication API - Specific methods for auth operations
 */
export const authApi = {
  /**
   * Validate current session
   * @returns {Promise} User session data
   */
  validate: async () => {
    return api.get('/api/v1/auth/validate');
  },

  /**
   * Get current user info
   * @returns {Promise} User data
   */
  me: async () => {
    return api.get('/api/v1/auth/me');
  },

  /**
   * Google OAuth callback
   * @param {string} code - OAuth authorization code
   * @returns {Promise} Auth response with session
   */
  googleCallback: async (code) => {
    return api.post('/api/v1/auth/google/callback', { code });
  },

  /**
   * Logout
   * @returns {Promise} Logout confirmation
   */
  logout: async () => {
    return api.post('/api/v1/auth/logout');
  },

  /**
   * Get tenant subscription
   * @param {string} tenantId - Tenant ID
   * @returns {Promise} Subscription data
   */
  getSubscription: async (tenantId) => {
    return api.get(`/api/v1/tenant/${tenantId}/subscription`);
  },
};

/**
 * Tenant API - Tenant-specific operations
 */
export const tenantApi = {
  /**
   * Get tenant details
   * @param {string} tenantId - Tenant ID
   * @returns {Promise} Tenant data
   */
  getTenant: async (tenantId) => {
    return api.get(`/api/v1/tenants/${tenantId}`);
  },

  /**
   * Update tenant
   * @param {string} tenantId - Tenant ID
   * @param {Object} data - Update data
   * @returns {Promise} Updated tenant
   */
  updateTenant: async (tenantId, data) => {
    return api.put(`/api/v1/tenants/${tenantId}`, data);
  },

  /**
   * Get tenant features
   * @param {string} tenantId - Tenant ID
   * @returns {Promise} Feature flags
   */
  getFeatures: async (tenantId) => {
    return api.get(`/api/v1/tenant/${tenantId}/features`);
  },
};

/**
 * User API - User management operations
 */
export const userApi = {
  /**
   * Get users for tenant
   * @param {string} tenantId - Tenant ID
   * @returns {Promise} List of users
   */
  getUsers: async (tenantId) => {
    return api.get(`/api/v1/users?tenant_id=${tenantId}`);
  },

  /**
   * Invite user
   * @param {Object} data - Invitation data
   * @returns {Promise} Invitation result
   */
  inviteUser: async (data) => {
    return api.post('/api/v1/users/invite', data);
  },

  /**
   * Update user role
   * @param {string} userId - User ID
   * @param {Object} data - Role update data
   * @returns {Promise} Updated user
   */
  updateUserRole: async (userId, data) => {
    return api.put(`/api/v1/users/${userId}/role`, data);
  },
};

/**
 * Debug helper - test API connectivity
 */
if (typeof window !== 'undefined') {
  window.debugZuperBooksApi = async () => {
    console.group('🔍 API Connectivity Test');
    
    try {
      console.log('Testing backend URL:', config.backendUrl);
      
      // Test health endpoint
      const health = await api.get('/api/v1/health');
      console.log('✅ Health Check:', health);
      
      // Test routing
      const routing = await api.get('/api/v1/test-routing');
      console.log('✅ Routing Test:', routing);
      
      console.log('✅ All API tests passed!');
    } catch (error) {
      console.error('❌ API Test Failed:', error.message);
      console.error('Error details:', {
        url: error.config?.url,
        status: error.response?.status,
        data: error.response?.data,
      });
    }
    
    console.groupEnd();
  };
}

export default api;
